export const CHARACTER_IDS = ["dragon", "unicorn"] as const;
export type CharacterId = (typeof CHARACTER_IDS)[number];

export const WORLD_IDS = ["volcano", "rainbow"] as const;
export type WorldId = (typeof WORLD_IDS)[number];

export type ProjectileKind = "fireball" | "rainbow-beam";

export type EnemyKind =
  | "little-knight"
  | "young-dragon"
  | "knight-mage"
  | "playful-goblin"
  | "friendly-ghost"
  | "ghost-mage";

export type SceneryKind =
  | "basalt"
  | "cave"
  | "volcano"
  | "lava"
  | "fire-crystal"
  | "rainbow"
  | "cloud"
  | "heart"
  | "star"
  | "light-path";

export type HazardKind = "lava" | "fall" | "storm-cloud";

export interface ThemePalette {
  readonly sky: string;
  readonly ground: string;
  readonly primary: string;
  readonly secondary: string;
  readonly accent: string;
  readonly danger: string;
}

export interface WorldTheme {
  readonly id: WorldId;
  readonly characterId: CharacterId;
  readonly displayName: string;
  readonly palette: ThemePalette;
  readonly scenery: readonly SceneryKind[];
  readonly hazards: readonly HazardKind[];
  readonly enemies: readonly EnemyKind[];
  readonly rareCollectibleName: string;
  readonly celebration: string;
}

export interface CharacterDefinition {
  readonly id: CharacterId;
  readonly displayName: string;
  readonly worldId: WorldId;
  readonly projectile: ProjectileKind;
  readonly projectileName: string;
  readonly accessibilityLabel: string;
}

export const COLLECTIBLE_KINDS = [
  "coin",
  "gem",
  "wisdom-star",
  "heart",
] as const;
export type CollectibleKind = (typeof COLLECTIBLE_KINDS)[number];

export interface CollectibleInventory {
  readonly coins: number;
  readonly gems: number;
  readonly wisdomStars: number;
  readonly hearts: number;
}

export interface GameSettings {
  readonly locale: "he-IL";
  readonly musicVolume: number;
  readonly effectsVolume: number;
  readonly muted: boolean;
  readonly reducedMotion: boolean;
  readonly highContrast: boolean;
  readonly touchControls: boolean;
}

export interface Position {
  readonly x: number;
  readonly y: number;
}

export interface Checkpoint {
  readonly id: string;
  readonly index: number;
  readonly position: Position;
}

export type MathDifficulty = 1 | 2 | 3 | 4 | 5 | 6;
export type ArithmeticOperation = "count" | "add" | "subtract" | "compare";
export type ComparisonSymbol = "<" | "=" | ">";
export type MathAnswer = number | ComparisonSymbol;

export const SAVED_MATH_SKILL_IDS = [
  "counting",
  "sequence",
  "addition",
  "subtraction",
  "missing-number",
  "compare",
  "tens",
  "word-problems",
] as const;

export type SavedMathSkillId = (typeof SAVED_MATH_SKILL_IDS)[number];

export interface SavedMathSkillMastery {
  readonly currentTier: MathDifficulty;
  readonly recentResults: readonly boolean[];
  readonly consecutiveMisses: number;
  readonly supportActive: boolean;
  readonly totalAttempts: number;
  readonly totalCorrect: number;
}

export interface ArithmeticQuestion {
  readonly id: string;
  readonly prompt: string;
  readonly operation: ArithmeticOperation;
  readonly choices: readonly MathAnswer[];
  readonly correctAnswer: MathAnswer;
  readonly difficulty: MathDifficulty;
  readonly visualAid?: readonly number[];
}

export interface MathProgress {
  readonly difficulty: MathDifficulty;
  readonly correctAnswers: number;
  readonly incorrectAnswers: number;
  readonly recentResults: readonly boolean[];
  readonly masteryBySkill: Readonly<Record<SavedMathSkillId, SavedMathSkillMastery>>;
}

export interface RespawnRecord {
  readonly count: number;
  readonly reason: "damage" | "fall";
  readonly checkpointId: string;
  readonly position: Position;
}

export interface GameSession {
  readonly characterId: CharacterId;
  readonly worldId: WorldId;
  readonly levelId: string;
  readonly levelNumber: number;
  readonly health: number;
  readonly maxHealth: number;
  readonly invulnerableUntilMs: number;
  readonly checkpoint: Checkpoint;
  readonly respawn: RespawnRecord | null;
  readonly collectibles: CollectibleInventory;
  readonly collectedItemIds: readonly string[];
  readonly openedQuestionBoxIds: readonly string[];
  readonly solvedQuestionBoxIds: readonly string[];
  readonly mathProgress: MathProgress;
}

export interface LevelSummary {
  readonly levelId: string;
  readonly score: number;
  readonly collectibles: CollectibleInventory;
  readonly solvedQuestionBoxes: number;
}
